import { api } from '@/api/client'
import type {
  FtpConnectionType,
  FtpDiffChunk,
  FtpFileItem,
  FtpFileVersion,
  FtpRecentItem,
} from './types'

interface ApiSuccess<T = unknown> {
  success: boolean
  error?: string
}

export async function ftpConnect(connectionType: FtpConnectionType, host: string) {
  return api<ApiSuccess & { connectionId: string; protocol: string }>('/api/ftp/connect', {
    method: 'POST',
    body: { connectionType, host },
  })
}

export async function ftpDisconnect(connectionId: string) {
  return api<ApiSuccess>('/api/ftp/disconnect', {
    method: 'POST',
    body: { connectionId },
  })
}

export async function ftpList(connectionId: string, path: string) {
  const q = encodeURIComponent(path)
  return api<ApiSuccess & { items: FtpFileItem[] }>(`/api/ftp/list/${connectionId}?path=${q}`)
}

export async function ftpRead(connectionId: string, filePath: string) {
  const q = encodeURIComponent(filePath)
  return api<ApiSuccess & { content: string }>(`/api/ftp/read/${connectionId}?path=${q}`)
}

export async function ftpSave(connectionId: string, filePath: string, content: string) {
  return api<ApiSuccess>(`/api/ftp/save/${connectionId}`, {
    method: 'POST',
    body: { path: filePath, content },
  })
}

export async function ftpUpload(connectionId: string, remotePath: string, file: File) {
  const form = new FormData()
  form.append('file', file)
  form.append('path', remotePath)
  return api<ApiSuccess>(`/api/ftp/upload/${connectionId}`, {
    method: 'POST',
    body: form,
    skipJson: true,
  })
}

export async function ftpDelete(connectionId: string, itemPath: string, type: 'file' | 'directory') {
  return api<ApiSuccess>(`/api/ftp/delete/${connectionId}`, {
    method: 'DELETE',
    body: { path: itemPath, type },
  })
}

export async function ftpRename(connectionId: string, itemPath: string, newName: string) {
  return api<ApiSuccess>(`/api/ftp/rename/${connectionId}`, {
    method: 'POST',
    body: { path: itemPath, newName },
  })
}

export async function ftpMkdir(connectionId: string, parentPath: string, name: string) {
  return api<ApiSuccess>(`/api/ftp/mkdir/${connectionId}`, {
    method: 'POST',
    body: { path: parentPath, name },
  })
}

export async function ftpMkfile(connectionId: string, parentPath: string, name: string, content = '') {
  return api<ApiSuccess>(`/api/ftp/mkfile/${connectionId}`, {
    method: 'POST',
    body: { path: parentPath, name, content },
  })
}

export async function ftpCopy(
  connectionId: string,
  sourcePath: string,
  destPath: string,
  move: boolean,
) {
  return api<ApiSuccess>(`/api/ftp/copy/${connectionId}`, {
    method: 'POST',
    body: { sourcePath, destPath, move },
  })
}

export function ftpDownloadUrl(connectionId: string, filePath: string) {
  return `/api/ftp/download/${connectionId}?path=${encodeURIComponent(filePath)}`
}

export async function ftpDownloadBlob(connectionId: string, filePath: string) {
  const res = await fetch(ftpDownloadUrl(connectionId, filePath), { credentials: 'include' })
  if (!res.ok) {
    const data = await res.json().catch(() => null)
    throw new Error(
      (data && typeof data === 'object' && 'error' in data && String(data.error)) ||
        'Ошибка скачивания',
    )
  }
  return res.blob()
}

export async function ftpGetFavorites() {
  const data = await api<ApiSuccess & { favorites: string[] }>('/api/ftp/favorites')
  return {
    ...data,
    favorites: (data.favorites || []).map((path) => ({ path })),
  }
}

export async function ftpAddFavorite(path: string) {
  return api<ApiSuccess>('/api/ftp/favorites', { method: 'POST', body: { path } })
}

export async function ftpRemoveFavorite(path: string) {
  return api<ApiSuccess>('/api/ftp/favorites', { method: 'DELETE', body: { path } })
}

export async function ftpGetRecent() {
  return api<ApiSuccess & { recent: FtpRecentItem[] }>('/api/ftp/recent')
}

export async function ftpAddRecent(path: string, name: string, type: string) {
  return api<ApiSuccess>('/api/ftp/recent', {
    method: 'POST',
    body: { path, name, type },
  })
}

export async function ftpGetVersions(connectionId: string, filePath: string) {
  const q = encodeURIComponent(filePath)
  return api<ApiSuccess & { versions: FtpFileVersion[] }>(
    `/api/ftp/versions/${connectionId}?path=${q}`,
  )
}

export async function ftpRestoreVersion(
  connectionId: string,
  filePath: string,
  versionIndex: number,
) {
  return api<ApiSuccess>(`/api/ftp/versions/restore/${connectionId}`, {
    method: 'POST',
    body: { path: filePath, versionIndex },
  })
}

export async function ftpCompare(connectionId: string, path1: string, path2: string) {
  return api<ApiSuccess & { diff: FtpDiffChunk[] }>(`/api/ftp/compare/${connectionId}`, {
    method: 'POST',
    body: { path1, path2 },
  })
}

function parseJsonResponse(xhr: XMLHttpRequest) {
  try {
    return JSON.parse(xhr.responseText) as { success?: boolean; error?: string }
  } catch {
    return null
  }
}

export function ftpUploadWithProgress(
  connectionId: string,
  remotePath: string,
  file: File,
  onProgress: (percent: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    const form = new FormData()
    form.append('file', file)
    form.append('path', remotePath)

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100))
    }

    xhr.onload = () => {
      const data = parseJsonResponse(xhr)
      if (xhr.status >= 200 && xhr.status < 300 && data?.success !== false) {
        onProgress(100)
        resolve()
        return
      }
      reject(new Error(data?.error || 'Ошибка загрузки'))
    }

    xhr.onerror = () => reject(new Error('Ошибка сети при загрузке'))
    xhr.open('POST', `/api/ftp/upload/${connectionId}`)
    xhr.withCredentials = true
    xhr.send(form)
  })
}

export function ftpDownloadWithProgress(
  connectionId: string,
  filePath: string,
  onProgress: (percent: number) => void,
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('GET', ftpDownloadUrl(connectionId, filePath))
    xhr.withCredentials = true
    xhr.responseType = 'blob'

    xhr.onprogress = (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100))
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress(100)
        resolve(xhr.response as Blob)
        return
      }
      reject(new Error('Ошибка скачивания'))
    }

    xhr.onerror = () => reject(new Error('Ошибка сети при скачивании'))
    xhr.send()
  })
}
